// Trustline validation service
import { trustline, TrustlineInitOptions, TrustlineWeb3ValidateParams, TrustlineValidateResponse } from '@trustline.id/websdk';

class TrustlineService {
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  // NOTE: Currently only validateWithdraw is active
  // validateDeposit and validateRefund are commented out in components but kept for future use

  private initialize() {
    try {
      const initOptions: TrustlineInitOptions = {
        clientId: process.env.REACT_APP_TRUSTLINE_CLIENT_ID || 'YOUR_TRUSTLINE_CLIENT_ID',
        loginUri: process.env.REACT_APP_TRUSTLINE_LOGIN_URI || 'https://yourapp.com/auth/trustline/callback'
      };
      trustline.init(initOptions);
      this.isInitialized = true;
      console.log('Trustline SDK initialized');
    } catch (error) {
      console.error('Failed to initialize Trustline SDK:', error);
    }
  }

  async validateTransaction(data: TrustlineWeb3ValidateParams): Promise<TrustlineValidateResponse> {
    if (!this.isInitialized) {
      throw new Error('Trustline SDK not initialized');
    }

    try {
      const response = await trustline.validate(data);
      return response;
    } catch (error) {
      console.error('Trustline validation failed:', error);
      throw error;
    }
  }

  async validateDeposit(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    tokenAddress: string,
    amount: string,
    beneficiary: string
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: tokenAddress === '0x0000000000000000000000000000000000000000' ? amount : '0',
        data: {
          functionSelector: 'depose(address,address,uint256)',
          args: [tokenAddress, beneficiary, amount]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateWithdraw(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'withdraw(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Withdraw transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Withdraw transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Withdraw transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }

  async validateRefund(
    chainId: number,
    senderAddress: string,
    contractAddress: string,
    depositId: number
  ): Promise<boolean> {
    try {
      const validationData: TrustlineWeb3ValidateParams = {
        chainId: chainId.toString(),
        senderAddress,
        contractAddress,
        nativeAmount: '0',
        data: {
          functionSelector: 'refund(uint256)',
          args: [depositId.toString()]
        }
      };

      const response = await this.validateTransaction(validationData);

      if ('error' in response) {
        console.error('Trustline validation error:', response.error);
        return false;
      }

      switch (response.result.status) {
        case 'approved':
          console.log('Refund transaction approved by Trustline');
          return true;
        case 'approval_required':
          console.log('Refund transaction requires manual approval');
          return false;
        case 'rejected':
          console.log('Refund transaction rejected by Trustline:', response.result.reason);
          return false;
        default:
          console.log('Unknown validation status:', (response.result as any).status);
          return false;
      }
    } catch (error) {
      console.error('Trustline validation failed:', error);
      return false;
    }
  }
}

export const trustlineService = new TrustlineService();
